import React, { useEffect, useState } from 'react'
import TrimbleLoginModal from '@/app/core/components/TrimbleLoginModal'
import { TokenData } from '@/integrations/trimble/types'
import { Spinner } from '@/dc-shared-web/src'
import ProjectSightModal from '@/app/core/components/ProjectSight/ProjectSightModal'
import { getTrimbleAuthManager } from '@/integrations/trimble/trimbleApi'
import { useEnvContext } from 'next-runtime-env'
import {
  useActivityService,
  ActivityCategory,
  ActivitySubCategory,
  ActivityItemType,
} from '@dc-shared-web/hooks/api/activity'

interface ChecklistOption {
  label: string
  fieldName: string
  className: string
  show: boolean
  id: string
  optionGroup: 1 | 2 | 3
  questionOptions: {
    label: string
    fieldName: string
  }[]
  additionalOptions: {
    label: string
    fieldName: string
    show?: boolean
  }[]
}

interface PlaybookOption {
  label: string
  fieldName: string
  className: string
  show: boolean
  id: string
  additionalOptions: never[]
}

interface ProjectSightModalWrapperProps {
  open: boolean
  onOpenChange?: (open: boolean) => void
  documentName?: string
  crunchUUID?: string
  playbookOptions?: PlaybookOption[]
  checklistOptions?: ChecklistOption[]
}

/**
 * Wrapper component for ProjectSight modal
 * Handles authentication with Trimble before showing the export modal
 */
const ProjectSightModalWrapper: React.FC<ProjectSightModalWrapperProps> = ({
  open,
  onOpenChange,
  documentName,
  crunchUUID,
  checklistOptions,
  playbookOptions,
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null)
  const [showLoginModal, setShowLoginModal] = useState(false)
  const { NEXT_PUBLIC_PROJECTSIGHT_AUTH_URL } = useEnvContext()
  const { trackActivity } = useActivityService()

  // Check authentication status when modal is opened
  useEffect(() => {
    if (open) {
      checkAuthentication()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const checkAuthentication = async () => {
    try {
      const authManager = getTrimbleAuthManager(
        NEXT_PUBLIC_PROJECTSIGHT_AUTH_URL || ''
      )
      const userInfo = await authManager.getUserInfoDirect()
      setIsAuthenticated(!!userInfo)

      if (!userInfo) {
        setShowLoginModal(true)
      } else {
        setShowLoginModal(false)
      }
    } catch (error) {
      console.error('Failed to check authentication:', error)
      setIsAuthenticated(false)
      setShowLoginModal(true)
    }
  }

  // eslint-disable-next-line no-unused-vars
  const handleLoginSuccess = (tokenData: TokenData) => {
    setIsAuthenticated(true)
    setShowLoginModal(false)

    // Track successful authentication
    trackActivity(ActivityCategory.CRUNCH, ActivitySubCategory.DOWNLOADED, {
      itemGuid: crunchUUID || '',
      itemType: ActivityItemType.CRUNCH,
      success: true,
      data: {
        download_type: 'projectsight',
        document_name: documentName || '',
        event: 'trimble_authentication_success',
      },
    })
  }

  const handleLoginError = (error: Error) => {
    setIsAuthenticated(false)
    // Don't close the main modal if login fails
    // Keep the login modal open for the user to try again

    // Track authentication error
    trackActivity(ActivityCategory.CRUNCH, ActivitySubCategory.DOWNLOADED, {
      itemGuid: crunchUUID || '',
      itemType: ActivityItemType.CRUNCH,
      success: false,
      data: {
        download_type: 'projectsight',
        document_name: documentName || '',
        event: 'trimble_authentication_failed',
        error_message: error.message,
      },
    })
  }

  // Render loading spinner when authentication status is being checked
  const renderSpinner = () => {
    return (
      <div className='tw-fixed tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-white tw-bg-opacity-75 tw-z-50'>
        <Spinner className='tw-h-12 tw-w-12' />
      </div>
    )
  }

  // If authentication status is still being checked, show loading spinner
  if (open && isAuthenticated === null) {
    return renderSpinner()
  }

  return (
    <>
      {/* Show login modal if not authenticated */}
      <TrimbleLoginModal
        data-testid='trimble-login-modal'
        isOpen={showLoginModal}
        onClose={() => {
          setShowLoginModal(false)

          // Track when user closes the login modal without authenticating
          trackActivity(
            ActivityCategory.CRUNCH,
            ActivitySubCategory.DOWNLOADED,
            {
              itemGuid: crunchUUID || '',
              itemType: ActivityItemType.CRUNCH,
              success: false,
              data: {
                download_type: 'projectsight',
                document_name: documentName || '',
                event: 'trimble_authentication_modal_closed',
              },
            }
          )
        }}
        onSuccess={handleLoginSuccess}
        onError={handleLoginError}
      />

      {/* Show ProjectSight modal if user is authenticated */}
      {isAuthenticated && (
        <ProjectSightModal
          open={open}
          setOpen={(isOpen) => {
            // Track when user closes the modal after authenticating
            if (!isOpen && open) {
              trackActivity(
                ActivityCategory.CRUNCH,
                ActivitySubCategory.DOWNLOADED,
                {
                  itemGuid: crunchUUID || '',
                  itemType: ActivityItemType.CRUNCH,
                  success: true,
                  data: {
                    download_type: 'projectsight',
                    document_name: documentName || '',
                    event: 'modal_closed_after_authentication',
                  },
                }
              )
            }

            // Call the original onOpenChange function if provided
            if (onOpenChange) {
              onOpenChange(isOpen)
            }
          }}
          documentName={documentName}
          crunchUUID={crunchUUID}
          checklistOptions={checklistOptions}
          playbookOptions={playbookOptions}
        />
      )}
    </>
  )
}

export default ProjectSightModalWrapper
